import { AudioResponseDto, Gender, ScenarioPersonaDto } from '@/api-gen/api.schemas';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, current } from '@reduxjs/toolkit';
import { ChatState } from 'app/models/chat/chatState.ts';
import { TranscriptMesssage } from 'app/models/chat/transcript.ts';
import { getGenderFromSex, getPortrait } from 'app/utils/getPortrait.tsx';
import { getAvatarUrlWithSas } from 'app/utils/avatarUtils.ts';
import logger from 'app/utils/logger.ts';


export type AudioChatState = {
  sessionId: string;
  isRecording: boolean;
  transcript: TranscriptMesssage[];
  chatState: ChatState;
  currentPersona: ScenarioPersonaDto | null;
  currentAvatarUrl: string | null;
  isPlayingIntro: boolean;
  lastBlockId: number;
  blockHistory: number[];
};

const initialState: AudioChatState = {
  sessionId: '',
  isRecording: false,
  transcript: [],
  chatState: ChatState.Idle,
  currentPersona: null,
  currentAvatarUrl: null,
  isPlayingIntro: false,
  lastBlockId: 0,
  blockHistory: []
};

export const audioChatSlice = createSlice({
  name: 'AUDIO_CHAT',
  initialState,
  reducers: {
    addTranscriptMessage(state, action: PayloadAction<TranscriptMesssage>) {
      state.transcript.push(action.payload);
    },
    addCallerResponse(state, action: PayloadAction<{ audioResponse: AudioResponseDto; defaultGender: Gender }>) {
      let name = 'Caller';
      const persona = action.payload.audioResponse.persona;
      const avatarUrl = action.payload.audioResponse.avatarUrl ?? persona?.persona?.avatarUrl ?? null;

      if (persona) {
        name = persona.roleName ?? 'Caller';
        state.currentPersona = persona;
      } else {
        state.currentPersona = null;
      }

      // Store the avatar URL for use in TalkingAI component
      state.currentAvatarUrl = avatarUrl;

      // Use dynamic avatar URL if available, otherwise fall back to hardcoded avatars
      const avatarSrc = avatarUrl
        ? getAvatarUrlWithSas(avatarUrl)
        : getPortrait(
            'flat',
            persona?.persona?.gender ?? (persona as unknown as { gender?: Gender })?.gender ?? getGenderFromSex(action.payload.defaultGender)
          );

      const responseText = action.payload.audioResponse.text?.trim();
      if (!responseText) {
        state.chatState = ChatState.Idle;
        return;
      }

      const responseMessage: TranscriptMesssage = {
        text: responseText,
        name,
        isUser: false,
        date: new Date(),
        avatarSrc
      };
      state.transcript.push(responseMessage)
      state.chatState = ChatState.Speaking;
      const lastBlockId = action.payload?.audioResponse?.lastBlockId;
      if (lastBlockId) {
        state.lastBlockId = lastBlockId;
        state.blockHistory.push(lastBlockId);
      }
    },
    startTranscribingAudio(state) {
      state.chatState = ChatState.Transcribing;
    },
    userSpeechStart(state) {
      state.chatState = ChatState.Listening;
    },
    userSpeechFinished(state) {
      state.chatState = ChatState.Translating;
    },
    translatedSpeechReady(state) {
      state.chatState = ChatState.Speaking;
    },
    translatedSpeechFinished(state) {
      state.chatState = ChatState.Idle;
    },
    toggleRecording(state) {
      logger.log('toggle recording');
      if (state.isRecording) {
        state.chatState = ChatState.Idle;
      }
      state.isRecording = !state.isRecording;
    },
    setSessionId(state, action: PayloadAction<string>) {
      state.sessionId = action.payload;
      state.lastBlockId = 0;
      state.blockHistory = [];
    },
    endChat(state) {
      state.isRecording = false;
      state.chatState = ChatState.Idle
      state.currentAvatarUrl = null;
    },
    resetChatState() {
      return {
        ...initialState,
        transcript: [],
        blockHistory: [],
      };
    },
    resetTranscript(state) {
      state.transcript = [];
      state.chatState = ChatState.Idle
      state.sessionId = '';
      state.currentAvatarUrl = null;
    },
    playIntro(state) {
      state.isPlayingIntro = true;
    },
    endIntro(state) {
      state.isPlayingIntro = false;
    },
    replaceLastTranscription(state, action: PayloadAction<TranscriptMesssage>) {
      if (state.transcript.length > 0) {
        state.transcript[state.transcript.length - 1] = action.payload;
      }
    },
    transcriptionFailedReset(state) {
      logger.info('[AudioChatSlice] Transcription failed. Resetting chat state to Idle.');
      // Reset to Idle state to allow user to try speaking again.
      // isReadySelector will become true because Idle is included.
      state.chatState = ChatState.Idle;
    }
  },
});
